import { useState } from "react";
import { DropdownItem, EmptyDropdownItem, DatePicker } from "@components";
import { DATE_PRESETS, datePresetValue, resolveDateFilterValue } from "@utils";

const getDatePart = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  return dateTimeStr.split("T")[0];
};

const getTimePart = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  return dateTimeStr.split("T")[1] || "";
};

const combineDateAndTime = (date, time) => {
  if (!date) return "";
  return time ? `${date}T${time}` : date;
};

const hours = Array.from({ length: 24 }, (_, i) => i);
const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const pad = (n) => String(n).padStart(2, "0");

const TimeSelect = ({ value, onChange, disabled }) => {
  const parts = (value || "00:00").split(":");
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;

  return (
    <div className="flex items-center gap-1 mt-1">
      <select
        className={`border rounded px-2 py-1 ${disabled ? "opacity-50 bg-gray-100" : ""}`}
        value={h}
        disabled={disabled}
        onChange={(e) => onChange(`${pad(Number(e.target.value))}:${pad(m)}`)}
      >
        {hours.map((hour) => (
          <option key={hour} value={hour}>
            {pad(hour)}
          </option>
        ))}
      </select>
      <span className="text-sm font-medium">:</span>
      <select
        className={`border rounded px-2 py-1 ${disabled ? "opacity-50 bg-gray-100" : ""}`}
        value={m}
        disabled={disabled}
        onChange={(e) => onChange(`${pad(h)}:${pad(Number(e.target.value))}`)}
      >
        {minutes.map((min) => (
          <option key={min} value={min}>
            {pad(min)}
          </option>
        ))}
      </select>
    </div>
  );
};

const DateWithTime = ({ dateValue, timeValue, onDateChange, onTimeChange }) => (
  <div>
    <DatePicker value={dateValue} onChange={onDateChange} />
    <TimeSelect
      value={timeValue}
      onChange={onTimeChange}
      disabled={!dateValue}
    />
  </div>
);

const DatePickerByOperator = ({ value, onChange }) => {
  const handleDateChange = (field) => (date) => {
    const time = getTimePart(value?.[field]);
    onChange({ ...value, [field]: combineDateAndTime(date, time) });
  };

  const handleTimeChange = (field) => (time) => {
    const date = getDatePart(value?.[field]);
    onChange({ ...value, [field]: combineDateAndTime(date, time) });
  };

  switch (value?.operator) {
    case "before":
      return (
        <DateWithTime
          dateValue={getDatePart(value?.to)}
          timeValue={getTimePart(value?.to)}
          onDateChange={(date) => {
            const time = getTimePart(value?.to);
            onChange({
              ...value,
              to: combineDateAndTime(date, time),
              from: "",
            });
          }}
          onTimeChange={handleTimeChange("to")}
        />
      );
    case "after":
      return (
        <DateWithTime
          dateValue={getDatePart(value?.from)}
          timeValue={getTimePart(value?.from)}
          onDateChange={(date) => {
            const time = getTimePart(value?.from);
            onChange({
              ...value,
              from: combineDateAndTime(date, time),
              to: "",
            });
          }}
          onTimeChange={handleTimeChange("from")}
        />
      );
    case "is":
      return (
        <DatePicker
          value={value?.to || ""}
          onChange={(date) => onChange({ ...value, from: date, to: date })}
        />
      );
    case "between":
      return (
        <div className="flex flex-col gap-2">
          <div>
            <label className="text-xs text-gray-500">From</label>
            <DateWithTime
              dateValue={getDatePart(value?.from)}
              timeValue={getTimePart(value?.from)}
              onDateChange={handleDateChange("from")}
              onTimeChange={handleTimeChange("from")}
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">To</label>
            <DateWithTime
              dateValue={getDatePart(value?.to)}
              timeValue={getTimePart(value?.to)}
              onDateChange={handleDateChange("to")}
              onTimeChange={handleTimeChange("to")}
            />
          </div>
        </div>
      );
    default:
      return null;
  }
};

const DateEditor = ({ value, onChange }) => {
  const [showCustom, setShowCustom] = useState(false);

  // Presets are stored by id (see utils/dateFilterPresets) so "Today" still means today when
  // the filter is reopened or restored from a saved view tomorrow.
  const choosePreset = (preset) => onChange(datePresetValue(preset));

  // The custom view shows a preset's current dates so there is something to edit from, but
  // only looking must not change the stored value. Nothing is committed until the user edits
  // a date or the operator; that edit turns the value into fixed dates, so the preset id is
  // dropped or it would overwrite the edited dates the next time the filter is evaluated.
  const displayValue = resolveDateFilterValue(value);
  const onCustomChange = (next) => {
    const { preset, ...fixed } = next || {};
    onChange(fixed);
  };

  if (!showCustom) {
    return (
      <>
        {DATE_PRESETS.map((preset) => (
          <DropdownItem key={preset.id} onClick={() => choosePreset(preset)}>
            {preset.label}
          </DropdownItem>
        ))}
        <DropdownItem onClick={() => setShowCustom(true)}>
          Custom date
        </DropdownItem>
      </>
    );
  }

  return (
    <EmptyDropdownItem>
      <div className="p-2">
        <div className="mb-2">
          <label className="block text-sm font-medium mb-1">
            Filter by Date and Time
          </label>
          <select
            className="border rounded px-2 py-1 w-full"
            value={displayValue?.operator || "between"}
            onChange={(e) =>
              onCustomChange({ ...displayValue, operator: e.target.value })
            }
          >
            <option value="is">is</option>
            <option value="between">is between</option>
            <option value="before">is before</option>
            <option value="after">is after</option>
          </select>
        </div>
        <DatePickerByOperator value={displayValue} onChange={onCustomChange} />
      </div>
    </EmptyDropdownItem>
  );
};

export default DateEditor;
